/**
 * FloatingToolbar - Barra de ferramentas flutuante
 * 
 * - Aparece por movimento do mouse na parte inferior
 * - Desaparece automaticamente após inatividade
 * - Contém: toggle Mentor, post-it, formatação, contador
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Bold, 
  Italic, 
  Underline,
  Star,
  Type,
  MoreHorizontal,
  Save,
  Undo,
  Redo,
  ChevronDown,
  Minus,
  Plus,
} from 'lucide-react';
import { WRITING_FONTS } from './WritingCanvas';

export default function FloatingToolbar({
  editor,
  wordCount = 0,
  onSave,
  onPostItClick,
  onMentorClick,
  onFontChange,
  currentFont = 'playfair',
  fontSize = 18,
  onFontSizeChange,
  saving = false,
  lastSaved = null,
  visible = true,
  autoHide = true,
  autoHideDelay = 3000,
}) {
  const [isVisible, setIsVisible] = useState(visible);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFontMenu, setShowFontMenu] = useState(false);
  const hideTimeoutRef = useRef(null);
  const toolbarRef = useRef(null);

  // Handle visibility based on mouse position
  const handleMouseActivity = useCallback(() => {
    setIsVisible(true);
    
    if (autoHide) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsExpanded(false);
      }, autoHideDelay);
    }
  }, [autoHide, autoHideDelay]);

  // Setup mouse move listener for auto-show
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Show toolbar when mouse is in bottom 20% of screen
      const windowHeight = window.innerHeight;
      const threshold = windowHeight * 0.8;
      
      if (e.clientY > threshold) {
        handleMouseActivity();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [handleMouseActivity]);

  // Keep visible when hovering toolbar
  const handleToolbarMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
  };

  const handleToolbarMouseLeave = () => {
    if (autoHide) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsExpanded(false);
      }, autoHideDelay);
    }
  };

  // Format time
  const formatSavedTime = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (!editor) return null;

  return (
    <div
      ref={toolbarRef}
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        transition-all duration-300 ease-out
        ${isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-4 pointer-events-none'
        }
      `}
      onMouseEnter={handleToolbarMouseEnter}
      onMouseLeave={handleToolbarMouseLeave}
      data-testid="floating-toolbar"
    >
      <div className="flex items-center gap-1 px-3 py-2 bg-white/95 backdrop-blur-md rounded-xl shadow-lg shadow-black/10 border border-stone-200/50">
        
        {/* Post-it button */}
        <ToolbarButton
          onClick={onPostItClick}
          title="Adicionar nota"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="6" width="14" height="14" rx="1" fill="#fef08a" stroke="#eab308" strokeWidth="1"/>
              <path d="M14 16 L18 16 L18 20 Z" fill="#fde047"/>
              <circle cx="11" cy="5" r="3" fill="#dc2626"/>
            </svg>
          }
        />

        {/* Mentor toggle (star) */}
        <ToolbarButton
          onClick={onMentorClick}
          title="Pergunta de Ouro"
          className="text-amber-500 hover:text-amber-600"
          icon={<Star size={16} fill="currentColor" />}
        />

        {/* Divider */}
        <div className="w-px h-5 bg-stone-200 mx-1" />

        {/* Text formatting - collapsible */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Negrito (Ctrl+B)"
          icon={<Bold size={14} strokeWidth={2.5} />}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Itálico (Ctrl+I)"
          icon={<Italic size={14} strokeWidth={2.5} />}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Sublinhado (Ctrl+U)"
          icon={<Underline size={14} strokeWidth={2.5} />}
        />

        {/* More options */}
        {isExpanded && (
          <>
            <div className="w-px h-5 bg-stone-200 mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Desfazer (Ctrl+Z)"
              icon={<Undo size={14} />}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Refazer (Ctrl+Y)"
              icon={<Redo size={14} />}
            />
            
            {/* Font selector */}
            <div className="relative">
              <button
                onClick={() => setShowFontMenu(!showFontMenu)}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                title="Alterar fonte"
              >
                <Type size={12} />
                <span className="hidden sm:inline">{WRITING_FONTS[currentFont]?.label.split(' ')[0]}</span>
                <ChevronDown size={10} />
              </button>
              
              {showFontMenu && (
                <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-lg border border-stone-200 py-1 min-w-[160px] z-50">
                  {Object.entries(WRITING_FONTS).map(([key, font]) => (
                    <button
                      key={key}
                      onClick={() => {
                        onFontChange?.(key);
                        setShowFontMenu(false);
                      }}
                      className={`
                        w-full text-left px-3 py-2 text-sm transition-colors
                        ${currentFont === key 
                          ? 'bg-amber-50 text-amber-700' 
                          : 'text-stone-600 hover:bg-stone-50'
                        }
                      `}
                      style={{ fontFamily: font.family }}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Font size controls A- A+ */}
            <div className="flex items-center gap-0.5 ml-1">
              <button
                onClick={() => onFontSizeChange?.(-1)}
                disabled={fontSize <= 16}
                className={`
                  px-1.5 py-1 text-xs font-medium rounded transition-colors
                  ${fontSize <= 16 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-stone-100'}
                  text-stone-500
                `}
                title="Diminuir fonte (mín: 16px)"
              >
                A-
              </button>
              <span className="text-[10px] text-stone-400 w-6 text-center">{fontSize}</span>
              <button
                onClick={() => onFontSizeChange?.(1)}
                disabled={fontSize >= 24}
                className={`
                  px-1.5 py-1 text-xs font-medium rounded transition-colors
                  ${fontSize >= 24 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-stone-100'}
                  text-stone-500
                `}
                title="Aumentar fonte (máx: 24px)"
              >
                A+
              </button>
            </div>
          </>
        )}

        <ToolbarButton
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Menos opções' : 'Mais opções'}
          icon={<MoreHorizontal size={14} />}
        />

        {/* Divider */}
        <div className="w-px h-5 bg-stone-200 mx-1" />

        {/* Word count & save status */}
        <div className="flex items-center gap-2 pl-1">
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 transition-colors"
            title={lastSaved ? `Último salvo: ${formatSavedTime(lastSaved)}` : 'Salvar'}
          >
            <Save size={12} className={saving ? 'animate-pulse' : ''} />
            <span className="font-medium">
              {wordCount.toLocaleString('pt-BR')}
            </span>
            <span className="text-stone-400">palavras</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Individual toolbar button component
function ToolbarButton({ 
  onClick, 
  active = false, 
  disabled = false, 
  title, 
  icon, 
  className = '' 
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        p-2 rounded-lg transition-all duration-150
        ${active 
          ? 'bg-amber-100 text-amber-700' 
          : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
        }
        ${disabled ? 'opacity-40 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {icon}
    </button>
  );
}
